import css_text from '/srv/www/htdocs/loc/app/styles/web_components/button.css';

export class YalwaButtonBase extends HTMLElement {
	
	static get observedAttributes() {
		return [
			'intent', 'variant', 'size', 'shape', 'icon_layout',
			'full-width', 'disabled', 'autofocus',
			// ARIA attributes are forwarded to the inner <button>; they must be
			// observed so attributeChangedCallback fires when e.g. a dropdown
			// trigger sets aria-expanded at runtime.
			'aria-haspopup', 'aria-expanded', 'aria-controls', 'aria-describedby', 'aria-label', 'aria-labelledby'
		];
	}
	
	constructor() {
		super();
		if (!this.shadowRoot) {
			this.attachShadow({mode: 'open', delegatesFocus: true});
		}
		this.apply_css();
	}
	
	async apply_css() {
		await yalwa.basics.check_stylesheet_polyfill_needed();
		const sheet = new CSSStyleSheet();
		sheet.replaceSync(css_text + `
		::slotted(span[slot="icon_left"]),
		::slotted(span[slot="icon_right"]) {
			display: inline-flex;
			width: 24px;
			height: 24px;
			align-items: center;
			justify-content: center;
		}
		[data-size="xs"] {
			::slotted(span) {
				width: 20px;
				height: 20px;
			}
		}
		:host([icon_layout="both"]) {
			::slotted(div) {
				flex: 1;
				min-width: 0;
				text-align: left;
			}
		}`); // adding it here because this syntax breaks our ancient sass version
		this.shadowRoot.adoptedStyleSheets = [sheet];
	}
	
	connectedCallback() {
		this.upgrade_properties();
		this.render();
		jQuery(this).removeClass('invisible');
	}
	
	attributeChangedCallback(name, old_value, new_value) {
		if (name === 'autofocus') {
			this.core_element.toggleAttribute('autofocus', new_value !== null);
		}
		if (name.indexOf('aria-') === 0 && this.core_element) {
			// Forward live ARIA updates (e.g. aria-expanded from a dropdown
			// trigger) to the inner button — the element screen readers expose.
			if (new_value !== null) {
				this.core_element.setAttribute(name, new_value);
			} else {
				this.core_element.removeAttribute(name);
			}
			return;
		}
		if (old_value !== new_value) {
			this.render();
		}
	}
	
	upgrade_properties() {
		this.constructor.observedAttributes.forEach(prop => {
			if (this.hasOwnProperty(prop)) {
				let value = this[prop];
				delete this[prop];
				this[prop] = value;
			}
		});
	}
	
	render() {
		if (!this.core_element) {
			return;  // Guard until initialized by subclass
		}
		
		const $element = jQuery(this.core_element);
		// sync simple attributes that map directly to button attributes
		
		// set CSS state attributes for styling
		$element.attr({
			'data-intent': this.intent,
			'data-variant': this.variant || 'fill',
			'data-size': this.size || 'm',
			'data-shape': this.shape || 'rectangle',
			'data-icon_layout': this.icon_layout || 'default'
		});
		
		// handle boolean attributes
		$element.toggleClass('full-width', this.hasAttribute('full-width'));

		// forward host ARIA attributes to the inner button (the accessible
		// element) so triggers like the dropdown menu button can announce
		// expanded state; remove them here when dropped from the host
		['aria-haspopup', 'aria-expanded', 'aria-controls', 'aria-describedby', 'aria-label', 'aria-labelledby'].forEach(attr => {
			if (this.hasAttribute(attr)) {
				$element.attr(attr, this.getAttribute(attr));
			} else {
				$element.removeAttr(attr);
			}
		});
		jQuery(this).attr('aria-disabled', this.disabled ? 'true' : 'false');
	}
	
	get intent() {
		return this.getAttribute('intent') || 'primary';
	}
	
	set intent(value) {
		this.setAttribute('intent', value);
	}
	
	get variant() {
		return this.getAttribute('variant');
	}
	
	set variant(value) {
		this.setAttribute('variant', value);
	}
	
	get size() {
		return this.getAttribute('size');
	}
	
	set size(value) {
		this.setAttribute('size', value);
	}
	
	get shape() {
		return this.getAttribute('shape');
	}
	
	set shape(value) {
		this.setAttribute('shape', value);
	}
	
	get icon_layout() {
		return this.getAttribute('icon_layout');
	}
	
	set icon_layout(value) {
		this.setAttribute('icon_layout', value);
	}
	
	get full_width() {
		return this.hasAttribute('full-width');
	}
	
	set full_width(value) {
		if (value) {
			this.setAttribute('full-width', '');
		} else {
			this.removeAttribute('full-width');
		}
	}
	
	get disabled() {
		return this.hasAttribute('disabled');
	}
	
	set disabled(value) {
		if (value) {
			this.setAttribute('disabled', '');
		} else {
			this.removeAttribute('disabled');
		}
	}
	
	focus(options) { this.core_element?.focus(options); }
}
